import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './header.scss';
import logo from '../../assets/mv.png';
import LoginModal from '../../pages/account/LoginModal';
import RegisterModal from '../../pages/account/RegisterModal';
import defaultAvatar from '../../assets/avt.jpg';
import { FaUser, FaHeart, FaMoneyBillWave, FaSignOutAlt, FaHistory, FaChevronDown, FaBell } from 'react-icons/fa';
import { notificationService } from '../../Service/NotificationService';
import ChangePasswordModal from '../../pages/account/ChangePasswordModal';
import AvatarModal from '../../pages/account/AvatarModal';
import SearchBar from './SearchBar';
import { countryMovies } from '../../Redux/actions/MovieThunk';
import { useDispatch } from 'react-redux';
import { getUserByUsername } from '../../Redux/actions/UserThunk';

const Header = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const headerRef = useRef(null);
    const countryRef = useRef(null);

    const [userData, setUserData] = useState(() => {
        const s = localStorage.getItem('USER_LOGIN');
        return s ? JSON.parse(s) : null;
    });
    const [user, setUser] = useState({});
    const [countries, setCountries] = useState([]);
    const [showCountryDrop, setShowCountryDrop] = useState(false);
    const [showUserDrop, setShowUserDrop] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [siteLogo, setSiteLogo] = useState('');
    const [siteName, setSiteName] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);

    // Fetch countries
    useEffect(() => {
        dispatch(countryMovies()).then(r => { if (r) setCountries(r); }).catch(() => {});
    }, []);

    // Fetch user info
    useEffect(() => {
        if (!userData?.username) return;
        dispatch(getUserByUsername(userData.username)).then(r => { if (r) setUser(r); }).catch(() => {});
        const h = () => {
            // Đọc lại số dư từ localStorage (đã cập nhật khi nạp) — không phụ thuộc fetch (có thể 401 với user Google)
            try { const u = JSON.parse(localStorage.getItem('USER_LOGIN') || 'null'); if (u) setUserData(u); } catch (_) {}
            dispatch(getUserByUsername(userData.username)).then(r => { if (r) setUser(r); }).catch(() => {});
        };
        const onAvatarUpdated = (e) => setUserData(prev => prev ? { ...prev, avatarUrl: e.detail.avatar } : prev);
        window.addEventListener('balanceUpdated', h);
        window.addEventListener('avatarUpdated', onAvatarUpdated);
        return () => {
            window.removeEventListener('balanceUpdated', h);
            window.removeEventListener('avatarUpdated', onAvatarUpdated);
        };
    }, [userData?.username]);

    // Mở menu avatar -> luôn lấy số dư mới nhất (tránh hiện số dư cũ sau khi nạp)
    useEffect(() => {
        if (showUserDrop && userData?.username) {
            dispatch(getUserByUsername(userData.username)).then(r => { if (r) setUser(r); }).catch(() => {});
        }
    }, [showUserDrop]);

    // Fetch unread notification count
    useEffect(() => {
        if (!userData) return;
        const fetchCount = () => {
            notificationService.getUnreadCount()
                .then(res => { if (res?.data !== undefined) setUnreadCount(Number(res.data)); })
                .catch(() => {});
        };
        fetchCount();
        const interval = setInterval(fetchCount, 60000); // poll mỗi 60 giây
        const onRead = () => fetchCount();
        window.addEventListener('notificationRead', onRead);
        return () => { clearInterval(interval); window.removeEventListener('notificationRead', onRead); };
    }, [userData]);

    // Site logo/name from settings
    useEffect(() => {
        fetch('/movies-settings-api.php').then(r => r.json()).then(d => {
            if (!d.success) return;
            const map = {};
            d.data.forEach(row => { map[row.setting_key] = row.setting_value; });
            if (map['site_logo']) setSiteLogo(map['site_logo']);
            if (map['site_name']) setSiteName(map['site_name']);
        }).catch(() => {});
    }, []);

    // Transparent at top only on home page; always dark on other pages
    const isHome = pathname === '/' || pathname === '';
    useEffect(() => {
        if (!isHome) {
            headerRef.current?.classList.remove('at-top');
            return;
        }
        const h = () => {
            const y = window.scrollY;
            headerRef.current?.classList.toggle('at-top', y <= 10);
            headerRef.current?.classList.toggle('shrink', y > 60);
        };
        h();
        window.addEventListener('scroll', h);
        return () => window.removeEventListener('scroll', h);
    }, [isHome]);

    // Click outside country dropdown
    useEffect(() => {
        const h = (e) => {
            if (countryRef.current && !countryRef.current.contains(e.target)) setShowCountryDrop(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const handleLogout = () => { localStorage.clear(); setUserData(null); window.location.href = '/'; };

    const navItems = [
        { label: 'Trang chủ', href: '/' },
        { label: 'Chủ đề', href: '/genre' },
        { label: 'Hội viên', href: '/membership' },
        { label: 'Xem Chung', href: '/xem-chung', badge: 'NEW' },
        { label: 'Trực Tiếp', href: '/livestream', badge: 'LIVE' },
        { label: 'Diễn viên', href: '/actors' },
    ];

    const mobileNavItems = [
        { label: 'Trang chủ', icon: '🏠', href: '/' },
        { label: 'Trực Tiếp', icon: '📡', href: '/livestream' },
        { label: 'Xem Chung', icon: '🎬', href: '/xem-chung' },
        { label: 'Thể loại', icon: '🎭', href: '/genre' },
        { label: 'Hội viên', icon: '⭐', href: '/membership' },
    ];

    return (
        <>
            <header
                ref={headerRef}
                className="site-header at-top"
                onMouseEnter={() => isHome && headerRef.current?.classList.add('hovered')}
                onMouseLeave={() => headerRef.current?.classList.remove('hovered')}
            >
                <div className="site-header__inner">

                    {/* Logo */}
                    <a href="/" className="sh-logo">
                        <img src={siteLogo || logo} alt="logo" />
                        <span>{siteName || 'CT36 Movie'}</span>
                    </a>

                    {/* Search */}
                    <div className="sh-search">
                        <SearchBar />
                    </div>

                    {/* Nav */}
                    <nav className="sh-nav">
                        {navItems.map((item, i) => (
                            <a
                                key={i}
                                href={item.href}
                                className={`sh-nav__item${pathname === item.href ? ' active' : ''}`}
                            >
                                {item.label}
                                {item.badge && <span className={`sh-nav__badge${item.badge === 'LIVE' ? ' sh-nav__badge--live' : ''}`}>{item.badge}</span>}
                            </a>
                        ))}

                        {/* Quốc gia dropdown */}
                        <div className="sh-nav__item sh-dropdown" ref={countryRef}>
                            <button
                                className={`sh-nav__item sh-dropbtn${showCountryDrop ? ' open' : ''}`}
                                onClick={() => setShowCountryDrop(v => !v)}
                            >
                                Quốc gia <FaChevronDown className="sh-chevron" />
                            </button>
                            {showCountryDrop && (
                                <div className="sh-dropmenu sh-dropmenu--country">
                                    {countries.map((c, i) => (
                                        <a
                                            key={i}
                                            href={`/search/country/${encodeURIComponent(c)}`}
                                            className="sh-dropmenu__item"
                                            onClick={() => setShowCountryDrop(false)}
                                        >{c}</a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </nav>

                    {/* User area */}
                    <div className="sh-user">
                        {userData ? (
                            <div className="sh-user-menu">
                                <button className="sh-avatar-btn" onClick={() => setShowUserDrop(v => !v)}>
                                    {/* Avatar với badge thông báo */}
                                    <div style={{ position: 'relative', flexShrink: 0 }}>
                                        <img src={userData.avatarUrl || defaultAvatar} alt="avatar" className="sh-avatar" />
                                        {unreadCount > 0 && (
                                            <span style={{
                                                position: 'absolute', top: -3, right: -3,
                                                background: '#ef4444', color: '#fff',
                                                fontSize: 9, fontWeight: 700, lineHeight: 1,
                                                minWidth: 15, height: 15, borderRadius: 999,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                padding: '0 2px', border: '1.5px solid #0a0c12',
                                                pointerEvents: 'none',
                                            }}>
                                                {unreadCount > 99 ? '99+' : unreadCount}
                                            </span>
                                        )}
                                    </div>
                                    <span className="sh-username">{userData.fullName}</span>
                                    <FaChevronDown className={`sh-chevron${showUserDrop ? ' open' : ''}`} />
                                </button>
                                {showUserDrop && (
                                    <div className="sh-dropmenu sh-dropmenu--user">
                                        <div className="sh-dropmenu__header">
                                            <div className="sh-dm-balance">
                                                💰 {Number(user?.balance ?? userData?.balance ?? 0).toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </div>
                                            {(() => {
                                                const vipExp = user?.vipExpireDate ?? userData?.vipExpireDate ?? userData?.vip_expiry;
                                                return vipExp && new Date(vipExp) > new Date() ? (
                                                    <div className="sh-dm-vip">
                                                        ⭐ VIP đến {new Date(vipExp).toLocaleDateString('vi-VN')}
                                                    </div>
                                                ) : null;
                                            })()}
                                        </div>
                                        <Link to="#" className="sh-dropmenu__item" onClick={() => { setShowAvatarModal(true); setShowUserDrop(false); }}>
                                            <FaUser /> Đổi ảnh đại diện
                                        </Link>
                                        <Link to="#" className="sh-dropmenu__item" onClick={() => { setIsChangePasswordModalOpen(true); setShowUserDrop(false); }}>
                                            <FaUser /> Đổi mật khẩu
                                        </Link>
                                        <Link to="/account" className="sh-dropmenu__item" onClick={() => setShowUserDrop(false)}>
                                            <FaUser /> Quản lý tài khoản
                                        </Link>
                                        <Link to="/favortie" className="sh-dropmenu__item" onClick={() => setShowUserDrop(false)}>
                                            <FaHeart /> Yêu thích
                                        </Link>
                                        <Link to="/history" className="sh-dropmenu__item" onClick={() => setShowUserDrop(false)}>
                                            <FaHistory /> Lịch sử xem
                                        </Link>
                                        <Link to="/thong-bao" className="sh-dropmenu__item" onClick={() => setShowUserDrop(false)}
                                            style={{ position: 'relative' }}>
                                            <FaBell />
                                            Thông báo
                                            {unreadCount > 0 && (
                                                <span style={{
                                                    marginLeft: 'auto', background: '#ef4444', color: '#fff',
                                                    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
                                                }}>
                                                    {unreadCount}
                                                </span>
                                            )}
                                        </Link>
                                        <Link to="/payment" className="sh-dropmenu__item" onClick={() => setShowUserDrop(false)}>
                                            <FaMoneyBillWave /> Nạp tiền
                                        </Link>
                                        <button className="sh-dropmenu__item sh-dropmenu__logout" onClick={handleLogout}>
                                            <FaSignOutAlt /> Đăng xuất
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button className="sh-login-btn" onClick={() => setShowLoginModal(true)}>
                                <FaUser /> <span>Đăng nhập</span>
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* Mobile bottom navigation */}
            <nav className="sh-mobile-nav">
                {mobileNavItems.map((item, i) => {
                    const isActive = item.href === '/'
                        ? pathname === '/' || pathname === ''
                        : pathname.startsWith(item.href);
                    return (
                        <a key={i} href={item.href} className={`sh-mobile-nav__item${isActive ? ' active' : ''}`}>
                            <span className="sh-mobile-nav__icon">{item.icon}</span>
                            <span className="sh-mobile-nav__label">{item.label}</span>
                        </a>
                    );
                })}
            </nav>

            <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onSwitchToRegister={() => { setShowLoginModal(false); setShowRegisterModal(true); }} />
            <RegisterModal isOpen={showRegisterModal} onClose={() => setShowRegisterModal(false)} onSwitchToLogin={() => { setShowRegisterModal(false); setShowLoginModal(true); }} />
            <ChangePasswordModal isOpen={isChangePasswordModalOpen} onClose={() => setIsChangePasswordModalOpen(false)} />
            {showAvatarModal && (
                <AvatarModal
                    currentAvatar={userData?.avatarUrl}
                    onClose={() => setShowAvatarModal(false)}
                    onUpdated={(newUrl) => setUserData(prev => prev ? { ...prev, avatarUrl: newUrl } : prev)}
                />
            )}
        </>
    );
};

export default Header;
